import { supabase } from './supabase';

export interface JobSearchCriteria {
  keywords?: string[];
  title?: string;
  location?: string;
  remoteType?: 'remote' | 'hybrid' | 'onsite';
  salaryMin?: number;
  salaryMax?: number;
  employmentType?: string;
  experienceLevel?: string;
  skills?: string[];
  industry?: string;
}

export interface ScrapedJob {
  id: string;
  source_id: string;
  external_id: string;
  title: string;
  company: string;
  location: string;
  remote_type: string;
  salary_min: number | null;
  salary_max: number | null;
  salary_currency: string;
  employment_type: string;
  description: string;
  requirements: string | null;
  benefits: string | null;
  url: string;
  posted_date: string;
  expires_date: string | null;
  skills_required: string[];
  experience_level: string;
  industry: string;
  is_active: boolean;
  scraped_at: string;
}

export interface JobSource {
  id: string;
  name: string;
  source_type: string;
  base_url: string;
  is_active: boolean;
}

export interface AIAssistantMessage {
  id?: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  metadata?: any;
  created_at?: string;
}

export interface AISession {
  id: string;
  user_id: string;
  session_name: string | null;
  context_data: any;
  preferences: any;
  last_activity: string;
  created_at: string;
}

export class AICareerAssistantService {
  async createSession(userId: string, sessionName?: string): Promise<AISession | null> {
    const { data, error } = await supabase
      .from('ai_assistant_sessions')
      .insert({
        user_id: userId,
        session_name: sessionName || 'Job Search Session',
        context_data: {},
        preferences: {},
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating session:', error);
      return null;
    }

    return data;
  }

  async getActiveSessions(userId: string): Promise<AISession[]> {
    const { data, error } = await supabase
      .from('ai_assistant_sessions')
      .select('*')
      .eq('user_id', userId)
      .order('last_activity', { ascending: false })
      .limit(10);

    if (error) {
      console.error('Error fetching sessions:', error);
      return [];
    }

    return data || [];
  }

  async getSessionMessages(sessionId: string): Promise<AIAssistantMessage[]> {
    const { data, error } = await supabase
      .from('ai_assistant_messages')
      .select('*')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error fetching messages:', error);
      return [];
    }

    return data || [];
  }

  async addMessage(
    sessionId: string,
    role: 'user' | 'assistant' | 'system',
    content: string,
    metadata?: any
  ): Promise<AIAssistantMessage | null> {
    const { data, error } = await supabase
      .from('ai_assistant_messages')
      .insert({
        session_id: sessionId,
        role,
        content,
        metadata: metadata || {},
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding message:', error);
      return null;
    }

    await supabase
      .from('ai_assistant_sessions')
      .update({ last_activity: new Date().toISOString() })
      .eq('id', sessionId);

    return data;
  }

  async searchJobs(criteria: JobSearchCriteria): Promise<ScrapedJob[]> {
    let query = supabase
      .from('scraped_jobs')
      .select('*')
      .eq('is_active', true)
      .order('posted_date', { ascending: false });

    if (criteria.title) {
      query = query.ilike('title', `%${criteria.title}%`);
    }

    if (criteria.location) {
      query = query.ilike('location', `%${criteria.location}%`);
    }

    if (criteria.remoteType) {
      query = query.eq('remote_type', criteria.remoteType);
    }

    if (criteria.salaryMin) {
      query = query.gte('salary_min', criteria.salaryMin);
    }

    if (criteria.salaryMax) {
      query = query.lte('salary_max', criteria.salaryMax);
    }

    if (criteria.employmentType) {
      query = query.eq('employment_type', criteria.employmentType);
    }

    if (criteria.experienceLevel) {
      query = query.eq('experience_level', criteria.experienceLevel);
    }

    if (criteria.industry) {
      query = query.eq('industry', criteria.industry);
    }

    if (criteria.skills && criteria.skills.length > 0) {
      query = query.overlaps('skills_required', criteria.skills);
    }

    const { data, error } = await query.limit(100);

    if (error) {
      console.error('Error searching jobs:', error);
      return [];
    }

    return data || [];
  }

  async getJobSources(): Promise<JobSource[]> {
    const { data, error } = await supabase
      .from('job_sources')
      .select('id, name, source_type, base_url, is_active')
      .eq('is_active', true)
      .order('name', { ascending: true });

    if (error) {
      console.error('Error fetching job sources:', error);
      return [];
    }

    return data || [];
  }

  async logSearchQuery(
    userId: string,
    sessionId: string,
    queryText: string,
    criteria: JobSearchCriteria,
    sourcesSearched: string[],
    resultsCount: number
  ): Promise<void> {
    await supabase.from('job_search_queries').insert({
      user_id: userId,
      session_id: sessionId,
      query_text: queryText,
      parsed_criteria: criteria,
      sources_searched: sourcesSearched,
      results_count: resultsCount,
    });
  }

  async createJobAlert(
    userId: string,
    alertName: string,
    searchCriteria: JobSearchCriteria,
    sources: string[],
    frequency: 'realtime' | 'daily' | 'weekly'
  ): Promise<any> {
    const { data, error } = await supabase
      .from('job_alerts')
      .insert({
        user_id: userId,
        alert_name: alertName,
        search_criteria: searchCriteria,
        sources,
        frequency,
        is_active: true,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating job alert:', error);
      return null;
    }

    return data;
  }

  async getUserAlerts(userId: string): Promise<any[]> {
    const { data, error} = await supabase
      .from('job_alerts')
      .select('*')
      .eq('user_id', userId)
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching alerts:', error);
      return [];
    }

    return data || [];
  }

  async updateSessionName(sessionId: string, newName: string): Promise<boolean> {
    const { error } = await supabase
      .from('ai_assistant_sessions')
      .update({ session_name: newName })
      .eq('id', sessionId);

    if (error) {
      console.error('Error updating session name:', error);
      return false;
    }

    return true;
  }

  async deleteSession(sessionId: string): Promise<boolean> {
    try {
      await supabase
        .from('ai_assistant_messages')
        .delete()
        .eq('session_id', sessionId);

      const { error } = await supabase
        .from('ai_assistant_sessions')
        .delete()
        .eq('id', sessionId);

      if (error) {
        console.error('Error deleting session:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error deleting session and messages:', error);
      return false;
    }
  }

  parseNaturalLanguageQuery(query: string): JobSearchCriteria {
    const criteria: JobSearchCriteria = {};
    const lowerQuery = query.toLowerCase();

    if (lowerQuery.includes('remote')) {
      criteria.remoteType = 'remote';
    } else if (lowerQuery.includes('hybrid')) {
      criteria.remoteType = 'hybrid';
    } else if (lowerQuery.includes('onsite') || lowerQuery.includes('on-site')) {
      criteria.remoteType = 'onsite';
    }

    if (lowerQuery.includes('entry level') || lowerQuery.includes('junior')) {
      criteria.experienceLevel = 'entry';
    } else if (lowerQuery.includes('senior')) {
      criteria.experienceLevel = 'senior';
    } else if (lowerQuery.includes('mid level') || lowerQuery.includes('mid-level')) {
      criteria.experienceLevel = 'mid';
    }

    if (lowerQuery.includes('full time') || lowerQuery.includes('full-time')) {
      criteria.employmentType = 'full_time';
    } else if (lowerQuery.includes('part time') || lowerQuery.includes('part-time')) {
      criteria.employmentType = 'part_time';
    } else if (lowerQuery.includes('contract')) {
      criteria.employmentType = 'contract';
    }

    const salaryMatch = query.match(/\$?(\d+)k?/gi);
    if (salaryMatch && salaryMatch.length >= 1) {
      const amounts = salaryMatch.map((m) => {
        const num = parseInt(m.replace(/[\$k,]/g, ''));
        return m.toLowerCase().includes('k') ? num * 1000 : num;
      });
      if (amounts.length === 1) {
        criteria.salaryMin = amounts[0];
      } else if (amounts.length >= 2) {
        criteria.salaryMin = Math.min(...amounts);
        criteria.salaryMax = Math.max(...amounts);
      }
    }

    const locationMatch = query.match(/in\s+([A-Za-z\s,]+?)(?:\s|$|,)/i);
    if (locationMatch) {
      criteria.location = locationMatch[1].trim();
    }

    return criteria;
  }

  async generateResponse(_userMessage: string, searchResults: ScrapedJob[]): Promise<string> {
    if (searchResults.length === 0) {
      return `I searched across major job boards (Indeed, LinkedIn, Glassdoor, ZipRecruiter, Monster), company career pages, industry-specific boards, and government sites, but I didn't find any jobs matching your criteria right now.\n\nWould you like me to:\n1. Set up a job alert so I can notify you when matching positions are posted?\n2. Broaden the search criteria?\n3. Suggest similar roles that might interest you?`;
    }

    const topJobs = searchResults.slice(0, 15);
    let response = `Great news! I found ${searchResults.length} opportunities by searching across:\n\n`;
    response += `Major Job Boards: Indeed, LinkedIn, Glassdoor, ZipRecruiter, Monster\n`;
    response += `Industry-Specific Sites: Including nonprofit, tech, and education boards\n`;
    response += `Company Career Pages: Direct from employer websites\n`;
    response += `Government Sites: Public sector opportunities\n\n`;
    response += `Here are the top ${Math.min(15, searchResults.length)} matches:\n\n`;

    topJobs.forEach((job, idx) => {
      response += `${idx + 1}. ${job.title} at ${job.company}\n`;
      response += `📍 ${job.location} ${job.remote_type === 'remote' ? '(Remote)' : job.remote_type === 'hybrid' ? '(Hybrid)' : ''}\n`;
      if (job.salary_min && job.salary_max) {
        response += `💰 $${(job.salary_min / 1000).toFixed(0)}k-$${(job.salary_max / 1000).toFixed(0)}k\n`;
      }
      if (job.skills_required.length > 0) {
        response += `🔧 Skills: ${job.skills_required.slice(0, 3).join(', ')}\n`;
      }
      response += `🔗 [View Job](${job.url})\n\n`;
    });

    if (searchResults.length > 15) {
      response += `\n...and ${searchResults.length - 15} more opportunities!\n\n`;
    }

    response += `Would you like me to:\n`;
    response += `Show more details about any specific job?\n`;
    response += `Set up an alert for similar positions?\n`;
    response += `Help you refine your search?`;

    return response;
  }
}

export const aiAssistant = new AICareerAssistantService();
